import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Loader2, Pencil, Trash2, Check, X, Shapes, GripVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function Row({ c, editId, editName, setEditName, startEdit, saveEdit, cancelEdit, onDelete, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: c.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const editing = editId === c.id;
  return (
    <div ref={setNodeRef} style={style} data-testid={`category-row-${c.id}`} className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-slate-950">
      <button {...attributes} {...listeners} data-testid={`category-drag-${c.id}`} className="cursor-grab active:cursor-grabbing text-slate-300 hover:text-slate-500 touch-none"><GripVertical className="h-4 w-4" /></button>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-2">
            <Input data-testid={`category-edit-input-${c.id}`} value={editName} onChange={(e) => setEditName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveEdit(c); if (e.key === "Escape") cancelEdit(); }} className="h-9 max-w-xs" autoFocus />
            <button data-testid={`category-edit-save-${c.id}`} onClick={() => saveEdit(c)} className="text-emerald-600 hover:text-emerald-700 p-1"><Check className="h-4 w-4" /></button>
            <button data-testid={`category-edit-cancel-${c.id}`} onClick={cancelEdit} className="text-slate-400 hover:text-slate-600 p-1"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="flex items-center gap-2 font-medium text-slate-900 dark:text-white"><Shapes className="h-4 w-4 text-slate-400" /> {c.name}</div>
        )}
      </div>
      <button data-testid={`category-count-${c.id}`} onClick={() => onOpen(c)} title="View products in this category" className="shrink-0">
        <Badge variant="secondary" className="cursor-pointer hover:bg-indigo-100 dark:hover:bg-indigo-950">{c.product_count} product{c.product_count === 1 ? "" : "s"}</Badge>
      </button>
      {!editing && (
        <div className="shrink-0">
          <button data-testid={`category-edit-${c.id}`} onClick={() => startEdit(c)} className="text-slate-400 hover:text-blue-600 p-1.5" title="Rename"><Pencil className="h-4 w-4" /></button>
          <button data-testid={`category-delete-${c.id}`} onClick={() => onDelete(c)} className="text-slate-400 hover:text-red-600 p-1.5" title="Delete"><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

export default function Categories() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [toDelete, setToDelete] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = () => api.get("/categories").then((r) => setCats(r.data)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      await api.post("/categories", { name: newName.trim() });
      toast.success("Category added");
      setNewName("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setAdding(false); }
  };

  const saveEdit = async (c) => {
    if (!editName.trim()) return;
    try {
      await api.put(`/categories/${c.id}`, { name: editName.trim() });
      toast.success("Category renamed — products updated");
      setEditId(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/categories/${toDelete.id}`);
      toast.success("Category deleted");
      setToDelete(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIdx = cats.findIndex((c) => c.id === active.id);
    const newIdx = cats.findIndex((c) => c.id === over.id);
    const next = arrayMove(cats, oldIdx, newIdx);
    setCats(next);
    api.put("/categories/reorder", { ids: next.map((c) => c.id) })
      .then(() => toast.success("Order saved"))
      .catch((e) => { toast.error(formatApiErrorDetail(e.response?.data?.detail)); load(); });
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Categories</h1>
        <p className="text-slate-500 mt-1">Drag to reorder — this sets the tab order on kiosks. Click a product count to view its products.</p>
      </div>

      <div className="flex items-end gap-2 max-w-md">
        <div className="flex-1">
          <label className="text-sm font-medium text-slate-700 dark:text-slate-300">New category</label>
          <Input data-testid="category-new-input" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") add(); }} placeholder="e.g. Confectionery" className="mt-1" />
        </div>
        <Button data-testid="category-add-button" onClick={add} disabled={adding || !newName.trim()} className="bg-blue-600 hover:bg-blue-700">
          {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Add</>}
        </Button>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden divide-y divide-slate-100 dark:divide-slate-800">
        {loading && <p className="text-center text-slate-400 py-8"><Loader2 className="h-5 w-5 animate-spin inline" /></p>}
        {!loading && !cats.length && <p className="text-center text-slate-400 py-8">No categories yet — add one above.</p>}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
          <SortableContext items={cats.map((c) => c.id)} strategy={verticalListSortingStrategy}>
            {cats.map((c) => (
              <Row key={c.id} c={c} editId={editId} editName={editName} setEditName={setEditName}
                startEdit={(x) => { setEditId(x.id); setEditName(x.name); }}
                saveEdit={saveEdit} cancelEdit={() => setEditId(null)}
                onDelete={setToDelete}
                onOpen={(x) => navigate(`/admin/categories/${encodeURIComponent(x.name)}`)} />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => { if (!o) setToDelete(null); }}>
        <AlertDialogContent data-testid="category-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Please confirm</AlertDialogTitle>
            <AlertDialogDescription>
              Delete category <span className="font-semibold text-slate-900 dark:text-white">{toDelete?.name}</span>?
              {toDelete?.product_count ? ` ${toDelete.product_count} product(s) still use it — you'll need to reassign them first.` : " This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="category-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="category-delete-confirm" onClick={(e) => { e.preventDefault(); doDelete(); }} className="bg-red-600 hover:bg-red-700 focus:ring-red-600">Delete category</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
